import type { ReactNode } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import SiteNav, { BookingButton, WriteMeLink } from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageSeo from "@/components/site/PageSeo";

export const PRICING_SLUG = "fractional-cmo-definition-missions-tarifs";
export const PRICING_PATH = `/blog/${PRICING_SLUG}`;
export const PRICING_TITLE = "Fractional CMO : tarifs, formats et missions en 2026 | CMO Partner";
export const PRICING_DESCRIPTION =
  "Combien coûte un Fractional CMO en 2026 ? Formats léger, standard et intensif, exemples de missions, comparaison avec un CDI et critères pour bien choisir.";
export const PRICING_H1 = "Fractional CMO : tarifs, formats et missions en 2026";
const URL = `https://www.cmopartner.fr${PRICING_PATH}`;

export const PRICING_FAQ = [
  { q: "Combien coûte un Fractional CMO ?", a: "Chez CMO Partner, un accompagnement démarre à partir de 1 500 € HT par mois. Le tarif dépend du format (léger : 2 à 4 jours par mois, standard : 4 à 6 jours, intensif : 8 à 12 jours), du niveau d'expertise requis et de la durée. Il est établi sur devis après un appel de cadrage." },
  { q: "Peut-on changer de format en cours de mission ?", a: "Oui. Beaucoup de missions démarrent par un mois intensif (audit et mise en place) puis passent en format léger pour le pilotage. Le format est réévalué au moins tous les trois mois." },
  { q: "Un Fractional CMO est-il adapté à une PME sans équipe marketing ?", a: "Oui, c'est même une situation fréquente. Il structure la fonction marketing de zéro : choix des outils, recrutement du premier profil marketing, mise en place des processus, puis transmission au futur responsable interne." },
  { q: "Quelle différence entre un Fractional CMO et un directeur marketing externalisé ?", a: "Les deux termes désignent la même réalité. « Directeur marketing externalisé » est l'appellation historique en France ; « Fractional CMO » vient du marché anglo-saxon et s'est répandu dans les scale-ups et les ETI." },
  { q: "Combien de temps faut-il pour voir les premiers résultats ?", a: "Les premières actions sont visibles en 30 à 45 jours (audit terminé, premières actions pilotées). Les KPIs business évoluent en général entre 60 et 90 jours. Une transformation durable du système marketing prend 3 à 6 mois." },
  { q: "Comment se termine une mission ?", a: "Soit par le recrutement d'un CMO interne (le CMO à temps partagé aide à définir le profil et accompagne l'intégration), soit par une réduction progressive vers un format léger de pilotage, soit par une fin de mission nette une fois les objectifs atteints." },
];

const PERSON = { "@type": "Person", name: "Tiphany Rigaudias", jobTitle: ["Fractional CMO", "Directrice marketing externalisée", "Directrice digitale externalisée"], knowsAbout: ["Marketing B2B", "CRM", "Marketing automation", "IA appliquée au marketing", "Formation des équipes marketing"], url: "https://www.cmopartner.fr/a-propos", sameAs: ["https://linkedin.com/in/tiphanyrigaudias"] };

export const PRICING_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: PRICING_H1,
    description: PRICING_DESCRIPTION,
    datePublished: "2026-09-28",
    dateModified: "2026-09-28",
    inLanguage: "fr-FR",
    author: PERSON,
    publisher: { "@type": "Organization", name: "CMO Partner", url: "https://www.cmopartner.fr/", founder: PERSON },
    mainEntityOfPage: { "@type": "WebPage", "@id": URL },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: PRICING_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://www.cmopartner.fr/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.cmopartner.fr/blog" },
      { "@type": "ListItem", position: 3, name: PRICING_H1, item: URL },
    ],
  },
];

const H2 = ({ children }: { children: string }) => (
  <h2 className="text-2xl font-bold leading-tight md:text-3xl">
    {children}
    <span className="text-primary">.</span>
  </h2>
);
const P = ({ children }: { children: ReactNode }) => <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{children}</p>;
const A = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="text-foreground underline underline-offset-4 hover:text-primary">{children}</Link>
);
const Bullets = ({ items }: { items: ReactNode[] }) => (
  <ul className="mt-5 space-y-3">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3 text-lg leading-relaxed text-muted-foreground">
        <span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);
const Table = ({ head, rows }: { head: string[]; rows: string[][] }) => (
  <div className="mt-8 overflow-x-auto rounded-lg border border-border">
    <table className="w-full min-w-[640px] text-left text-sm md:text-base">
      <thead className="bg-secondary">
        <tr>{head.map((h) => <th key={h} scope="col" className="px-5 py-4 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r[0]} className="border-t border-border">
            {r.map((c, i) => <td key={i} className={`px-5 py-4 align-top ${i === 0 ? "font-semibold" : "text-muted-foreground"}`}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const formats = [
  ["Léger", "2 à 4 jours par mois", "Définition de la stratégie d'acquisition, cadrage des priorités, pilotage mensuel avec le dirigeant", "PME qui ont une équipe opérationnelle mais pas de cap"],
  ["Standard", "4 à 6 jours par mois", "Pilotage du plan marketing, management de l'équipe et des prestataires, suivi des KPIs", "Entreprises en croissance sans direction marketing"],
  ["Intensif", "8 à 12 jours par mois", "Mise en place des automatisations CRM (scoring, nurturing, routage des leads), déploiement des outils", "Phases de structuration, de lancement ou de transformation"],
];
const comparison = [
  ["Rôle", "Direction opérationnelle", "Conseil et recommandations"],
  ["Livrables", "Stratégie et exécution", "Audit et plan d'action"],
  ["Position", "Manage l'équipe et les prestataires", "Externe, sans lien hiérarchique"],
  ["Durée", "6 à 12 mois en général", "1 à 8 semaines"],
  ["Indicateurs", "KPIs business (leads, CA, ROI)", "Livrables définis au départ"],
];
const missions: { title: string; text: string; link?: { to: string; label: string } }[] = [
  { title: "Growth Architecture", text: "Définition des leviers d'acquisition prioritaires, architecture du tunnel de conversion, indicateurs de performance, coordination des prestataires (agence SEO, ads, contenu).", link: { to: "/services/growth-architecture", label: "Voir la page Growth Architecture" } },
  { title: "Stratégie de contenu et positionnement", text: "Audit de la proposition de valeur, définition des personas acheteurs, plan éditorial, coordination des équipes de rédaction." },
  { title: "CRM & Lifecycle Marketing", text: "Audit CRM, déploiement ou refonte des automatisations email, lead scoring, alignement marketing-sales sur les critères de qualification.", link: { to: "/services/crm-lifecycle", label: "Voir la page CRM & Lifecycle" } },
  { title: "IA & Agentic Marketing", text: "Intégration des outils IA dans les processus marketing (personnalisation, contenu assisté, analyse prédictive) et formation des équipes.", link: { to: "/services/ai-marketing", label: "Voir la page AI Marketing" } },
  { title: "B2C et e-commerce", text: "Optimisation du tunnel e-commerce, stratégie de fidélisation, coordination marketplace et retail.", link: { to: "/fractional-cmo-b2c-ecommerce", label: "Voir la page Fractional CMO B2C" } },
];
const readNext = [
  { label: "CMO externalisé, agence ou CDI : le comparatif", to: "/blog/cmo-externalise-vs-agence-vs-recrutement" },
  { label: "Directeur marketing externalisé", to: "/directeur-marketing-externalise" },
  { label: "Fractional CMO à Bordeaux", to: "/fractional-cmo-bordeaux" },
];

const FractionalCmoPricingArticle = () => (
  <div className="min-h-screen bg-background text-foreground">
    <PageSeo path={PRICING_PATH} title={PRICING_TITLE} description={PRICING_DESCRIPTION} />
    <SiteNav />
    <main>
      <article>
        <header className="border-b border-border px-5 pb-14 pt-20 md:pt-28">
          <div className="mx-auto max-w-3xl">
            <nav className="mb-8 text-sm text-muted-foreground" aria-label="Fil d'Ariane">
              <Link to="/" className="hover:text-primary">Accueil</Link>
              <span className="px-2">/</span>
              <Link to="/blog" className="hover:text-primary">Blog</Link>
              <span className="px-2">/</span>
              <span className="text-foreground">{PRICING_H1}</span>
            </nav>
            <h1 className="text-4xl font-bold leading-[1.1] md:text-5xl">{PRICING_H1}</h1>
            <p className="mt-8 text-sm text-muted-foreground">
              Par <Link to="/a-propos" className="font-semibold text-foreground hover:text-primary">CMO Partner — Fractional CMO</Link> · Mis à jour : <time dateTime="2026-09-28">septembre 2026</time>
            </p>
            <P>Le Fractional CMO, ou directeur marketing à temps partagé, s'est imposé dans les PME et ETI françaises. La question qui revient le plus souvent chez les dirigeants est simple : combien cela coûte, et pour quoi exactement ? Ce guide détaille les formats d'accompagnement, des exemples concrets de missions et les critères pour choisir.</P>
          </div>
        </header>

        <div className="px-5 py-16 md:py-20">
          <div className="mx-auto max-w-3xl space-y-16">
            <section>
              <H2>Le Fractional CMO en bref</H2>
              <P>Un Fractional CMO est un directeur marketing senior qui intervient dans votre entreprise quelques jours par mois, sur une durée définie. Contrairement à un consultant, il ne se limite pas à des recommandations : il définit la stratégie, pilote son exécution, manage les prestataires et l'équipe, et rend compte au dirigeant. Pour une définition complète, consultez notre page <A to="/fractional-cmo">Fractional CMO</A> et la présentation du <A to="/directeur-marketing-externalise">directeur marketing à temps partagé</A>.</P>
            </section>

            <section>
              <H2>Les 3 formats d'accompagnement</H2>
              <Table head={["Format", "Rythme", "Exemple de mission", "Pour qui"]} rows={formats} />
              <h3 className="mt-10 text-xl font-bold">Un format qui évolue avec vos besoins</h3>
              <P>Le format n'est pas figé. Un schéma fréquent : un premier mois intensif pour l'audit, la priorisation et la mise en place des fondations (CRM, tunnel, indicateurs), puis un accompagnement léger pour piloter le système et arbitrer les priorités. L'intensité suit vos enjeux, pas un forfait standard.</P>
            </section>

            <section>
              <H2>Combien coûte un Fractional CMO ?</H2>
              <P>Chez CMO Partner, un accompagnement démarre à partir de <strong className="text-foreground">1 500 € HT par mois</strong>. Le tarif final dépend de trois facteurs :</P>
              <Bullets items={[
                "le nombre de jours par mois (format léger, standard ou intensif) ;",
                "le niveau d'expertise requis par la mission (stratégie, CRM et automatisation, IA, international) ;",
                "la durée de l'engagement.",
              ]} />
              <P>Le devis est établi après un appel de cadrage de 30 minutes. Ces montants s'entendent hors frais de prestataires, d'outils et de budget média.</P>
              <P>Pour comparaison, un CMO senior en CDI représente 80 à 150 k€ par an en coût total employeur, sans compter 3 à 6 mois de recrutement. Un accompagnement fractional ne représente qu'une fraction de ce budget, pour un niveau d'expertise équivalent et sans engagement long.</P>
              <div className="mt-10 rounded-lg bg-secondary p-7">
                <p className="text-lg font-semibold">Obtenez un devis adapté à votre situation en 30 minutes.</p>
                <div className="mt-5"><BookingButton label="Réserver un appel" location="blog_pricing_mid" /></div>
              </div>
            </section>

            <section>
              <H2>Ce que comprend une mission</H2>
              <Bullets items={[
                "Un diagnostic écrit de votre écosystème marketing (canaux, CRM, contenus, pipeline)",
                "Une feuille de route priorisée sur 90 jours",
                "Le pilotage des actions et des prestataires",
                "Un reporting régulier au dirigeant sur des KPIs business (leads qualifiés, taux de conversion, coût d'acquisition)",
                "La formation de l'équipe et la documentation, pour une autonomie en fin de mission",
              ]} />
            </section>

            <section>
              <H2>Les missions types d'un Fractional CMO</H2>
              <div className="mt-8 space-y-6">
                {missions.map((m) => (
                  <div key={m.title} className="border-t border-border pt-6">
                    <h3 className="text-xl font-bold">{m.title}</h3>
                    <p className="mt-3 text-lg leading-relaxed text-muted-foreground">
                      {m.text}{m.link && <> <A to={m.link.to}>{m.link.label}</A>.</>}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <H2>Fractional CMO ou consultant marketing : quelle différence ?</H2>
              <Table head={["Critère", "Fractional CMO", "Consultant marketing"]} rows={comparison} />
            </section>

            <section>
              <H2>Comment choisir son Fractional CMO ?</H2>
              <P>Cinq critères à vérifier :</P>
              <ol className="mt-5 space-y-4">
                {[
                  ["Une expérience dans votre secteur ou un secteur proche", "les enjeux d'un SaaS B2B et d'un e-commerce B2C sont très différents."],
                  ["La capacité à opérer, pas seulement à conseiller", "demandez des résultats concrets (leads générés, taux de conversion amélioré, outils déployés)."],
                  ["Une disponibilité réelle", "un Fractional CMO qui mène huit missions en parallèle ne sera pas disponible pour votre équipe."],
                  ["La compatibilité culturelle", "il travaillera avec vos équipes, pas seulement pour vous envoyer des présentations."],
                  ["Un cadre de mission clair", "objectifs définis, indicateurs de succès convenus, possibilité de sortie à 3 mois."],
                ].map(([t, d], i) => (
                  <li key={t} className="flex gap-4 text-lg leading-relaxed text-muted-foreground">
                    <span className="font-bold text-primary">{String(i + 1).padStart(2, "0")}</span>
                    <span><strong className="text-foreground">{t}</strong> : {d}</span>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </div>
      </article>

      <section className="border-t border-border bg-secondary px-5 py-20">
        <div className="mx-auto max-w-3xl">
          <H2>FAQ : tarifs et fonctionnement d'un Fractional CMO</H2>
          <div className="mt-10">
            {PRICING_FAQ.map((f) => (
              <details key={f.q} className="group border-t border-border py-6">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold">
                  {f.q}
                  <span className="text-primary transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border px-5 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-xl font-bold">À lire aussi</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {readNext.map((r) => (
              <Link key={r.to} to={r.to} className="rounded-lg border border-border p-5 transition-colors hover:border-primary">
                <span className="font-semibold">{r.label}</span>
                <ArrowRight className="mt-4 h-4 w-4 text-primary" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="booking" className="scroll-mt-16 bg-foreground px-5 py-24 text-center text-background md:py-28">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold md:text-5xl">Quel format pour votre entreprise<span className="text-primary"> ?</span></h2>
          <p className="mt-6 text-lg text-background/60">30 minutes pour cadrer vos enjeux et recevoir une proposition adaptée.</p>
          <div className="mt-10"><BookingButton label="Réserver un appel de 30 minutes" location="blog_pricing_final" large /><WriteMeLink /></div>
          <p className="mt-6 text-sm text-background/50">Basée à Bordeaux · Disponible partout en France</p>
        </div>
      </section>
    </main>
    <SiteFooter />
  </div>
);

export default FractionalCmoPricingArticle;
